import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { isProtectedRoute } from 'constants/routes';
import Cookies from 'js-cookie';
import transferLocalStorageDataToCookie from 'utils/cookies/transfer-local-storage-data-to-cookie';
import { USER_COOKIE_NAME, USER_EXPIRE_DAYS } from 'constants/cookies';
import { useUser as useNewUser, useAuth, useSessionGuard, deserializeUser } from '@bedrock/auth';
import { useProfile } from 'hooks/use-profile';
import { useSiteLocation } from 'hooks/use-site-location';
import { useTerms } from 'hooks/use-terms';
import { LOCALE_FORM_ERROR_PATHS, Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import jwt from 'jsonwebtoken';
import { IncomingMessage } from 'http';
import { getJSON, getObjectFromRequestCookie, getStringFromRequestCookie } from 'utils/cookies/request-cookies';
import { JWT_COOKIE_NAME } from 'constants/cookies';

import type { SignInResult, SignUpResult, ConfirmSignUpResult, User, ValidateUserRequest, ValidateUserResponse, AuthErrorResponse, SignUpProps } from '@bedrock/auth';
import type { Provider } from '@zoocasa/go-search';
import type { IUserLocation } from 'utils/user-location';
import type { CountryCode } from 'types/countries';
import type { SearchSuggestions } from 'components/suggested-location-dropdown';
import type { SearchParameters } from 'data/conversational-search/types';

// ─── Auth types (re-exported from @bedrock/auth for backward compatibility) ───

export type { User, ValidateUserRequest, ValidateUserTheme, ValidateUserResponse, AuthErrorResponse } from '@bedrock/auth';

type Success = { success: string };
type Error = { error: string };
type ForgotPasswordSuccess = { success: string; nextStep: 'confirm_in_app' | 'email_sent' };

export interface IUserContext {
  user: User | null;
  isAuthenticated: boolean;
  authenticateUser: (properties: Record<string, unknown>) => Promise<SignInResult | void>;
  registerUser: (properties: Record<string, unknown>) => Promise<SignUpResult>;
  confirmSignUp: (email: string, code: string) => Promise<ConfirmSignUpResult>;
  resendSignUpCode: (email: string) => Promise<ConfirmSignUpResult>;
  acceptedBrowserBasedTerms: Provider[];
  acceptedTerms: Provider[];
  acceptTerms: (providerId: Provider) => void;
  setZoocasaTermsStateToRead: () => void;
  areZoocasaTermsRead: boolean;
  confirmUser: (confirmationToken: string, userAgent: string, userLocationSlug: string, userLatitude: number, userLongitude: number) => Promise<Success | Error>;
  signOut: () => Promise<void>;
  submitPhone: (phone: string, profile?: { firstName: string; lastName: string }) => Promise<Success | Error>;
  recoverPasswordForEmail: (email: string) => Promise<ForgotPasswordSuccess | Error>;
  confirmForgotPassword: (email: string, code: string, password: string) => Promise<Success | Error>;
  startMigratedReset: (email: string) => Promise<Success | Error>;
  resetPassword: (params: { password: string; passwordConfirmation: string; resetPasswordToken: string }) => Promise<Success | Error>;
  updatePassword: (params: { currentPassword?: string; password: string; passwordConfirmation: string; email?: string }) => Promise<Success | Error>;
  updateUserData: (params: User) => Promise<Success | Error>;
  saveAndPersistUser: (user: User | null) => void;
  validateUser: (user: ValidateUserRequest) => Promise<ValidateUserResponse | AuthErrorResponse>;
  userLocation?: IUserLocation;
  siteLocation?: CountryCode;
  userLocationSameAsSiteLocation: boolean;
  handleSetSiteLocation: (countryCode: CountryCode) => void;
  handleSetDefaultUserLocation: (countryCode: CountryCode) => void;
  hasAcceptedConnRequestDisclaimers: boolean;
  handleConnRequestDisclaimers: (isChecked: { isChecked: boolean }) => void;
  userPopularCities: SearchSuggestions[];
  hasNavigatedFromInternalLink: boolean;
  setHasNavigatedFromInternalLink: (hasNavigatedFromInternalLink: boolean) => void;
  updateEmail: (params: { newEmail: string; retypeEmail: string; password?: string; passwordConfirmation?: string }) => Promise<Success | Error>;
  confirmEmailUpdate: (code: string) => Promise<Success | Error>;
  isOnSignUpUrl: boolean;
  setIsOnSignUpUrl: (isOnSignupUrl: boolean) => void;
  isCrawler: boolean;
  functionalCookiesEnabled: boolean;
  aiSearchParams: SearchParameters | null;
  setAiSearchParams: (params: SearchParameters | null) => void;
}

export const UserContext = createContext<IUserContext | Record<string, unknown>>({});
export function useUserContext <T extends IUserContext | Record<string, unknown> = IUserContext>() {
  return useContext(UserContext) as T;
}

// ─── Provider props ───────────────────────────────────────────────────────────

interface Props {
  user: IUserContext['user'];
  userLocation?: IUserLocation;
  storedSiteLocation?: CountryCode;
  storedConnRequestDisclaimers: boolean;
  storedUserPopularCities?: SearchSuggestions[];
  isCrawler: boolean;
  functionalCookiesEnabled?: boolean;
  children: ReactNode;
}

// ─── Composed provider ───────────────────────────────────────────────────────

export function UserContextProvider({
  user: userData,
  userLocation: storedUserLocation,
  storedSiteLocation,
  storedConnRequestDisclaimers,
  storedUserPopularCities,
  isCrawler,
  functionalCookiesEnabled,
  children,
}: Props) {
  transferLocalStorageDataToCookie(USER_COOKIE_NAME);

  // ── Auth (from new AuthSession) ──────────────────────────────────────────
  const { user: authUser, isLoaded, isSignedIn } = useNewUser();
  const authActions = useAuth();
  const router = useRouter();

  const user = (!isLoaded ? (authUser ?? userData) : authUser) as User | null;
  const isAuthenticated = isSignedIn;

  // ── Location ──────────────────────────────────────────────────────────────
  const location = useSiteLocation({
    storedUserLocation,
    storedSiteLocation,
    storedUserPopularCities,
    functionalCookiesEnabled,
  });

  // ── Terms & compliance ────────────────────────────────────────────────────
  const terms = useTerms(user?.id);

  // ── Profile operations ────────────────────────────────────────────────────
  const profile = useProfile(user);

  const { t } = useTranslation(Namespaces.FORM_ERROR);
  const { supportedFeatures } = authActions;

  useSessionGuard();

  const updatePassword: IUserContext['updatePassword'] = async params => {
    if (supportedFeatures.canChangePassword) {
      const result = await authActions.changePassword(params.currentPassword!, params.password);
      if (result.ok === true) {
        return { success: t(LOCALE_FORM_ERROR_PATHS.PASSWORD_CHANGED_SUCCESS, { defaultValue: 'Your password was changed successfully.' }) };
      }
      return { error: result.ok === false ? result.error : t(LOCALE_FORM_ERROR_PATHS.GENERAL_ERROR, { defaultValue: 'Something went wrong. Please try again later.' }) };
    }
    return profile.updatePassword(params);
  };

  // ── Compat auth actions (bridge old IUserContext to new AuthSession) ──────

  const authenticateUser: IUserContext['authenticateUser'] = async params => {
    try {
      const result = await authActions.signIn(params.email as string, params.password as string);
      if ('needsPhone' in result && result.needsPhone) {
        return { ok: false, error: 'Phone number required' } as any;
      }
      if (result.user) authActions.completeAuth(result.user);
      return { ok: true } as any;
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Sign-in failed' } as any;
    }
  };

  const registerUser: IUserContext['registerUser'] = async params => {
    try {
      const props: SignUpProps = {
        email: params.email as string,
        password: params.password as string,
        firstName: params.firstName as string,
        lastName: params.lastName as string,
        homePhone: params.homePhone as string | undefined,
        newsletter: params.newsletter as boolean | undefined,
        privacyTerms: params.privacyTerms as boolean | undefined,
        signupUrl: params.signupUrl as string | undefined,
        userCountry: params.userCountry as string | undefined,
        theme: params.theme as string | undefined,
      };
      const result = await authActions.signUp(props);
      if ('user' in result && result.user) authActions.completeAuth(result.user);
      return { ok: true } as any;
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Registration failed' } as any;
    }
  };

  const confirmSignUp: IUserContext['confirmSignUp'] = async (email, code) => {
    try {
      const result = await authActions.confirmSignUp(email, code);
      if ('user' in result && result.user) authActions.completeAuth(result.user);
      return { ok: true } as any;
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Confirmation failed' } as any;
    }
  };

  const resendSignUpCode: IUserContext['resendSignUpCode'] = async email => {
    try {
      await authActions.resendSignUpCode(email);
      return { ok: true } as any;
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Failed to resend code' } as any;
    }
  };

  const validateUser: IUserContext['validateUser'] = async request => {
    try {
      const result = await authActions.validateUser(request);
      return result as any;
    } catch {
      return { error: 'Validation failed' } as any;
    }
  };

  const submitPhone: IUserContext['submitPhone'] = async (phone, profile) => {
    const result = await authActions.submitPhone(phone, profile);
    return result.ok ? { success: 'Phone submitted' } : { error: (result as { ok: false; error: string }).error };
  };

  const recoverPasswordForEmail: IUserContext['recoverPasswordForEmail'] = async email => {
    try {
      const result = await authActions.forgotPassword(email, '');
      if (result.error) return { error: result.error };
      return { success: result.success ?? 'Reset email sent', nextStep: result.nextStep ?? 'email_sent' };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to send reset email' };
    }
  };

  const confirmForgotPassword: IUserContext['confirmForgotPassword'] = async (email, code, password) => {
    try {
      const result = await authActions.confirmForgotPassword(email, code, password, '');
      return result.error ? { error: result.error } : { success: 'Password reset successfully' };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Password reset failed' };
    }
  };

  const startMigratedReset: IUserContext['startMigratedReset'] = async email => {
    try {
      const result = await authActions.forgotPassword(email, '');
      return result.error ? { error: result.error } : { success: result.success ?? 'Reset email sent' };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to start reset' };
    }
  };

  const saveAndPersistUser = (newUser: User | null) => {
    if (newUser) authActions.setUser(newUser);
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !isAuthenticated) return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('requires_auth')) {
      url.searchParams.delete('requires_auth');
      window.history.replaceState(null, '', url.toString());
    }
  }, [isAuthenticated]);

  const wasAuthenticatedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!isLoaded) return;
    const wasAuthenticated = wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = isAuthenticated;

    if (wasAuthenticated === true && !isAuthenticated) {
      if (isProtectedRoute(router.pathname)) {
        router.replace('/');
      }
    }
  }, [isLoaded, isAuthenticated, router]);

  // ── UI flags (local to provider, no domain logic) ─────────────────────────
  const [hasAcceptedConnRequestDisclaimers, setHasAcceptedConnRequestDisclaimers] = useState(storedConnRequestDisclaimers);
  const [hasNavigatedFromInternalLink, setHasNavigatedFromInternalLink] = useState(false);
  const [isOnSignUpUrl, setIsOnSignUpUrl] = useState(false);
  const [aiSearchParams, setAiSearchParams] = useState<SearchParameters | null>(null);

  const handleConnRequestDisclaimers = ({ isChecked }: { isChecked: boolean }) => {
    if (isChecked) {
      Cookies.set('connection-request-disclaimers', 'true', { expires: USER_EXPIRE_DAYS });
      setHasAcceptedConnRequestDisclaimers(true);
    } else {
      Cookies.remove('connection-request-disclaimers');
      setHasAcceptedConnRequestDisclaimers(false);
    }
  };

  // ── Compose the full IUserContext value ────────────────────────────────────

  return (
    <UserContext.Provider
      value={{
        user,
        isAuthenticated,
        authenticateUser,
        registerUser,
        confirmSignUp,
        resendSignUpCode,
        validateUser,
        signOut: () => authActions.signOut(),
        submitPhone,
        recoverPasswordForEmail,
        confirmForgotPassword,
        startMigratedReset,
        saveAndPersistUser,

        siteLocation: location.siteLocation,
        userLocation: location.userLocation,
        userLocationSameAsSiteLocation: location.userLocationSameAsSiteLocation,
        userPopularCities: location.userPopularCities,
        handleSetSiteLocation: location.handleSetSiteLocation,
        handleSetDefaultUserLocation: location.handleSetDefaultUserLocation,

        acceptedBrowserBasedTerms: terms.acceptedBrowserBasedTerms,
        acceptedTerms: terms.acceptedTerms,
        acceptTerms: terms.acceptTerms,
        areZoocasaTermsRead: terms.areZoocasaTermsRead,
        setZoocasaTermsStateToRead: terms.setZoocasaTermsStateToRead,

        confirmUser: profile.confirmUser,
        resetPassword: profile.resetPassword,
        updatePassword,
        updateUserData: profile.updateUserData,
        updateEmail: profile.updateEmail,
        confirmEmailUpdate: profile.confirmEmailUpdate,

        hasAcceptedConnRequestDisclaimers,
        handleConnRequestDisclaimers,
        hasNavigatedFromInternalLink,
        setHasNavigatedFromInternalLink,
        isOnSignUpUrl,
        setIsOnSignUpUrl,
        isCrawler,
        functionalCookiesEnabled,
        aiSearchParams,
        setAiSearchParams,
      }}>
      {children}
    </UserContext.Provider>
  );
}

// ─── Standalone utilities (unchanged) ─────────────────────────────────────────

function useUser(): IUserContext {
  return useUserContext();
}

function getUserFromRequest(request: IncomingMessage): User | null {
  const raw = getObjectFromRequestCookie<Record<string, unknown>>(USER_COOKIE_NAME, request);
  return raw ? deserializeUser(raw) : null;
}

function isUserAuthenticatedFromRequest(request: IncomingMessage): boolean {
  const user = getUserFromRequest(request);
  if (!user || user?.jwt?.length === 0) {
    return false;
  }
  const jwtCookie = getStringFromRequestCookie(JWT_COOKIE_NAME, request);
  if (!jwtCookie) {
    return false;
  }
  const decodedUserJwt = jwt.decode(user.jwt, { complete: true, json: true });
  const decodedJwtCookie = jwt.decode(jwtCookie, { complete: true, json: true });
  if (decodedUserJwt && decodedUserJwt.payload?.['user_id'] && decodedJwtCookie && decodedJwtCookie.payload?.['user_id']) {
    const userJwt_user_id = decodedUserJwt.payload?.['user_id'];
    const jwtCookie_user_id = decodedJwtCookie.payload?.['user_id'];
    return String(user.id) === String(userJwt_user_id) && String(user.id) === String(jwtCookie_user_id);
  }
  return false;
}

function getUserFromCookies(): User | null {
  const cookie = Cookies.get(USER_COOKIE_NAME);
  if (cookie) {
    const raw = getJSON<Record<string, unknown>>(cookie);
    return raw ? deserializeUser(raw) : null;
  }
  return null;
}

function isValidJWTToken(token: string): boolean {
  try {
    if (!token || token.length === 0) {
      return false;
    }
    const decoded = jwt.decode(token, { complete: true, json: true });
    if (!decoded || !decoded.payload) {
      return false;
    }
    const payload = decoded.payload as any;
    if (!payload.user_id) {
      return false;
    }
    if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('JWT validation error:', error);
    return false;
  }
}

export { useUser, getUserFromRequest, getUserFromCookies, isUserAuthenticatedFromRequest, isValidJWTToken };
